import { inject } from '@angular/core';
import { CanMatchFn, Route, Router, UrlSegment, UrlTree } from '@angular/router';
import { FeatureFlagDxpService } from '@pure1/data';
import { map, Observable, of, switchMap, take } from 'rxjs';

import { EffectiveOrgService } from '../../data/services/effective-org.service';
import { extractOrgUidFromPath } from '../../data/utils/org-url';
import { OrgSwitcherService } from '../../page/page-wrapper/org-switcher/org-switcher.service';
import { NON_ORG_SCOPED_ROUTES, ORG_URL_FF } from './org-url-shared';

/**
 * canMatch on the bare `''` parent route. When the org-URL FF is on and the target
 * route is org-scoped, redirects to `/orgs/<uid>/...`. The URL is the source of truth
 * for the effective org; this guard resolves the uid in priority order:
 *   1. uid already present in the current URL (`router.url`)
 *   2. uid held by EffectiveOrgService (validator-primed from a previous nav)
 *   3. first org from the user's switcher list
 *   4. none available → `/no-role`
 *
 * Non-org-scoped routes (NON_ORG_SCOPED_ROUTES) and the FF-off case fall through.
 */
export const bareRouteMatchGuard: CanMatchFn = (
    _route: Route,
    segments: UrlSegment[],
): Observable<boolean | UrlTree> => {
    const featureFlagService = inject(FeatureFlagDxpService);
    const router = inject(Router);
    const effectiveOrgService = inject(EffectiveOrgService);
    const orgSwitcherService = inject(OrgSwitcherService);

    return featureFlagService.getFeatureFlag(ORG_URL_FF).pipe(
        switchMap(status => {
            if (!status?.enabled) return of(true);

            const firstSegment = segments[0]?.path;
            if (firstSegment && NON_ORG_SCOPED_ROUTES.has(firstSegment)) {
                return of(true);
            }

            return effectiveOrgService.effectiveOrg$.pipe(
                take(1),
                switchMap(effective => {
                    // router.url is the URL *before* this navigation — if the user was
                    // previously on /orgs/<uid>/... the uid is still readable here and
                    // used as the resolved org for the new navigation.
                    const currentUrlUid = extractOrgUidFromPath(router.url);
                    const resolved = currentUrlUid ?? effective?.uid ?? null;
                    if (resolved) {
                        return of(buildRedirect(router, resolved, segments));
                    }
                    // effectiveOrg$ is null only when the user has zero accessible orgs —
                    // primeEffectiveOrg deliberately skips setCurrentOrg in that case.
                    // Read userOrgs$ directly to distinguish that from "not yet primed".
                    return orgSwitcherService.userOrgs$.pipe(
                        take(1),
                        map(orgs => {
                            const uid = orgs[0]?.uid ?? null;
                            if (!uid) return router.parseUrl('/no-role');
                            return buildRedirect(router, uid, segments);
                        }),
                    );
                }),
            );
        }),
    );
};

function buildRedirect(router: Router, uid: string, segments: UrlSegment[]): UrlTree {
    const path = segments.map(s => s.path).join('/');
    const tree = router.parseUrl('/orgs/' + uid + (path ? '/' + path : ''));
    const queryParams = router.getCurrentNavigation()?.extractedUrl.queryParams;
    if (queryParams) {
        tree.queryParams = queryParams;
    }
    return tree;
}
